import { useQuery } from '@tanstack/react-query';
import {
  type ActionsEnumKey,
  configControllerGetBusinessRolePermissionsQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';

const allFalse = <T extends ActionsEnumKey>(actions: T[]) =>
  actions.reduce(
    (acc, action) => {
      acc[action] = false;
      return acc;
    },
    {} as Record<T, boolean>,
  );

export function useUserPermissions({
  productId,
  actions,
}: {
  productId?: string;
  actions: ActionsEnumKey[];
}) {
  const { data: user, isLoading: isUserLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  // @debt: if user is not a broker, using the first relationType might not be the best solution
  const businessRoleId = user?.brokerRole ?? user?.relations?.[0]?.relationType;

  const { data: permissions, isLoading: arePermissionsLoading } = useQuery({
    ...configControllerGetBusinessRolePermissionsQueryOptions({
      businessRoleId: businessRoleId!,
      productId,
    }),
    enabled: Boolean(businessRoleId),
  });

  const isLoading = isUserLoading || arePermissionsLoading;

  if (!user) {
    return { can: allFalse(actions), isLoading };
  }

  if (!permissions) {
    return {
      can: actions.reduce(
        (acc, action) => {
          acc[action] = user.isSuperUser;
          return acc;
        },
        {} as Record<ActionsEnumKey, boolean>,
      ),
      isLoading,
    };
  }

  const allowedPermissions = permissions.allowedPermissions.map(({ action }) => action);
  const forbiddenPermissions = permissions.forbiddenPermissions.map(({ action }) => action);

  return {
    can: actions.reduce(
      (acc, action) => {
        acc[action] =
          user.isSuperUser ||
          (allowedPermissions.includes(action) && !forbiddenPermissions.includes(action));

        return acc;
      },
      {} as Record<ActionsEnumKey, boolean>,
    ),
    isLoading,
  };
}
